import { Box, Button, Paper, Popover, Tooltip, Typography } from '@mui/material';
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { Event } from '@mui/icons-material';
import { format, parse, startOfWeek, getDay } from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";
import EventCard from './CommonComponents/EventCard';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/authContext';
import { APICallStatus, CalendarEvent, UserType } from '../Types/UnitFeatureTypes';
import { useDispatch, useSelector } from 'react-redux';
import { createEventAPIStatus, deleteEventAPIStatus, fetchEvents, fetchEventsCount } from '../Redux/selectors';
import { Actions } from '../Redux/Reducer/rootReducer';
import { useApp } from '../context/appContext';
import ViewTableHeader from './CommonComponents/ViewTableHeader';
import { useNavigate } from 'react-router-dom';

const Events = () => {

  const locales = {
    en: require("date-fns/locale/en-US"), // Add the locale for your region
  };
  
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const {user}  = useAuth();
  const eventsInformation = useSelector(fetchEvents);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [endDate, setEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1));

  const eventApi = useSelector(createEventAPIStatus);
  const eventDeleteApi = useSelector(deleteEventAPIStatus);
  const [event, setEvent] = useState<CalendarEvent>({
    id: -1,
    title: '',
    start_time: new Date(),
    end_time: new Date(),
    description: '',
    editable: true,
    creater: {
      id: user?.id || -1,
      name: user?.first_name + ' ' + user?.last_name || ''
    },
    location: '',
    all_day: false,
    capacity: 1000,
    image: ''
  });

  const {setSnackbar} = useApp();
  const IS_MANAGER = user?.type === UserType.MANAGER;

  useEffect(() => {
    if (eventApi === APICallStatus.SUCCESS) {
      dispatch(Actions.fetchEvent.eventActions.request({
        start_date: startDate,
        end_date: endDate
      }));
      setSnackbar(true, 'Event created successfully', 'success');
      dispatch(Actions.event.resetStatusRequestAction());
    } else if (eventApi === APICallStatus.FAILURE) {
      setSnackbar(true, 'Failed to create event', 'error');
      dispatch(Actions.event.resetStatusRequestAction());
    }
  }, [eventApi]);

  useEffect(() => {
    if (eventDeleteApi === APICallStatus.SUCCESS) {
      dispatch(Actions.fetchEvent.eventActions.request({
        start_date: startDate,
        end_date: endDate
      }));
      setSnackbar(true, 'Event deleted successfully', 'success');
      dispatch(Actions.event.resetStatusRequestAction());
    } else if (eventDeleteApi === APICallStatus.FAILURE) {
      setSnackbar(true, 'Failed to delete event', 'error');
      dispatch(Actions.event.resetStatusRequestAction());
    }
  }, [eventDeleteApi]);

  useEffect(() => {
    dispatch(Actions.fetchEvent.eventActions.request({
      start_date: startDate,
      end_date: endDate
    }));
  }, [startDate, endDate]);

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleCreateNewEvent = (value: Date, mouseEvent: React.MouseEvent<HTMLButtonElement>) => {
      setEvent({
        id: -1,
        title: '',
        start_time: value,
        end_time: value,
        description: '',
        editable: true,
        capacity: 1000,
        creater: {
          id: user?.id || -1,
          name: user?.first_name + ' ' + user?.last_name || ''
        },
        location: '',
        all_day: false,
        image: ''
      });
      setAnchorEl(mouseEvent.currentTarget);
    };

  const handleViewEvent = (event: CalendarEvent, mouseEvent: React.MouseEvent<HTMLButtonElement>) => {
    setEvent(event);
    setAnchorEl(mouseEvent.currentTarget);
  };

  const handleAddEvent = (event: React.MouseEvent<HTMLButtonElement>) => {
    navigate('/createEvent');
  }


  const addClickListener = () => {
    const target = document.getElementsByClassName('rbc-event');
    console.log(target);
  };

  
  return (
    <>
      {addClickListener()}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          anchorEl
        ? { top: anchorEl.getBoundingClientRect().top, left: anchorEl.getBoundingClientRect().left  - 350}
        : undefined
        }
      >
        <EventCard
          id={event.id}
          title={event.title}
          start_time={event.start_time}
          end_time={event.end_time}
          description={event.description}
          location={event.location}
          all_day={event.all_day}
          handleClose={handleClose}
          handleChange={setEvent}
          editable={event.editable}
          capacity={event.capacity}
          creater={event.creater}
          image={event.image}
        />
      </Popover>
    <Paper elevation={5} sx={{mb: 2, padding: 3, borderRadius: 3}}>
      {/* <Paper elevation={3} sx={{overflow: 'auto', mb: 2, padding: 2, borderRadius: 3, display: 'flex', justifyContent: 'space-between'}}>
        <Box color='primary.main' sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', }}>
            <Event sx={{ mr: 1, height: 40, width: 40 }} />
            <Typography variant="h5">Events</Typography>
        </Box>
    </Paper> */}
    <ViewTableHeader
        title="Events"
        icon={Event}
        handleAdd={handleAddEvent}
        addText="Add Event"
        showSort={false}
        allowAdd={IS_MANAGER}
    />
    <Box sx={{ height: 750, width: "100%" }}>
      <Calendar
        min={new Date()}
        localizer={localizer}
        events={eventsInformation.map((event) => {
        return {
          ...event,
          start_time: new Date(event.start_time),
          end_time: new Date(event.end_time)
        };
      })}
      tooltipAccessor="title"
      style={{ height: "100%", width: "100%" }}
      onNavigate={
        (date, view, action) => {
          if (action === 'NEXT' || action === 'PREV') {
            setStartDate(new Date(date.getFullYear(), date.getMonth(), 1));
            setEndDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
          }
        }
      }
      startAccessor="start_time"
      endAccessor="end_time"
      components={{
        event: ({ event }: { event: CalendarEvent }) => {
        return event ? <Button
          id="event"
          variant="text"
          onClick={(mouseEvent) => {
          if ((mouseEvent.target as HTMLElement).closest('#dateButton')) return;
          handleViewEvent(event as CalendarEvent, mouseEvent);
          }}
          sx={{ zIndex: 10, width: 200, fontSize: 12, color: 'white', textTransform: 'none', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 150 }}
        >
          {event.title}
        </Button> : null
        },
        // eventWrapper: (props) => {
        //   console.log("EVENT WRAPPER: ", props);
        //   const event = props.event as CalendarEvent;
        // return event ? <Tooltip title={
        //   <Typography>
        //   {event.title}
        //   <br />
        //   {event.start_time.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' })} - {event.end_time.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' })}
        //   </Typography>
        // }><Button 
        //     id="event"
        //     fullWidth
        //     variant="outlined"
        //     onClick={(mouseEvent) => {
        //     if ((mouseEvent.target as HTMLElement).closest('#dateButton')) return;
        //     handleViewEvent(event, mouseEvent);
        //     }}
        //     sx={{ zIndex: 10, width: 200, fontSize: 12, color: 'black', textTransform: 'none', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: 150 }}
        //   >
        //   {event.title}
        // </Button></Tooltip>  : null
        // },
      dateCellWrapper: ({ value }) => (
        <Button
          disabled={new Date(value) < new Date(Date.now() - 86400000) || !IS_MANAGER}
          id="dateButton"
          variant={new Date(value).toDateString() === new Date().toDateString() ? 'outlined' : 'text'}
          sx={{ width: 160, backgroundColor: new Date(value) < new Date(Date.now() - 86400000) ? '#ECECEC' : '' , zIndex: 4, fontSize: 13, textTransform: 'none', justifyContent: 'center', color: 'black'}}
          onClick={(e) => {
          if ((e.target as HTMLElement).closest('#event')) return;
          handleCreateNewEvent(value, e);
          }}
        />
      ),
      dayColumnWrapper: (props: any) => {
        const { children } = props;
        return <Paper elevation={3} sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid #ECECEC', backgroundColor: new Date(props.value) < new Date(Date.now() - 86400000) ? '#ECECEC' : '' }}>{children}</Paper>;
      },
      timeGutterHeader: (props: any) => {
        const { value } = props;
        return <Button
        variant="text"
        disabled
        sx={{ width: '100%', height: '100%', fontSize: 12, color: 'black' }}
        >
        {value}
        </Button>
      },
      timeGutterWrapper: (props: any) => {
        const { children } = props;
        return <Paper elevation={3} sx={{ height: '100%', width: 160, display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid #ECECEC', backgroundColor: '#f4f4f4' }}>{children}</Paper>;
      },
      timeSlotWrapper: (props: any) => {
        const { value, children } = props;
        return <Button>
        {value.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' })}
        </Button>
      },
      agenda: {
        event: ({ event }: { event: CalendarEvent }) => { 
        return event ? <Button 
          id="event"
          variant="text"
          onClick={(event) => {
          if ((event.target as HTMLElement).closest('#dateButton')) return;
          handleClick(event);
          }}
          sx={{ width: '100%', height: '100%', fontSize: 12, color: 'black' }}
        >
          {event.title}
          <br />
          {event.start_time.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' })} - {event.end_time.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' })}
        </Button> : null
        }
      },
      day: {
        header: ({ date }) => {
        return <Button
          variant="text"
          sx={{ width: 160, fontSize: 13, textTransform: 'none', justifyContent: 'center', color: 'black'}}
          >
          {date.toLocaleDateString()}
        </Button>
        },
        event: ({ event }: { event: CalendarEvent }) => { 
        return event ? <Button 
          id="event"
          variant="text"
          onClick={(event) => {
          if ((event.target as HTMLElement).closest('#dateButton')) return;
          handleClick(event);
          }}
          sx={{ width: '100%', height: '100%', fontSize: 12, color: 'black' }}
        >
          {event.title}
          <br />
          {event.start_time.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' })} - {event.end_time.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' })}
        </Button> : null
        }
      }
      }}
      timeslots={2}
      onShowMore={event => console.log(event)}
      />
      <style>
      {`
        .rbc-row-segment {
          z-index: 1;
        }
        .rbc-date-cell .rbc-button-link {
          font-weight: 600;
        }
        .rbc-timeslot-group {
          display: none;
        }
        .rbc-time-gutter .rbc-timeslot-group {
          display: flex;
        }
        .rbc-event {
          padding: 5px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: inline-block;
        }
        // .rbc-event:onClick {
        //   (mouseEvent) => {
        //     if ((mouseEvent.target as HTMLElement).closest('#dateButton')) return;
        //     handleViewEvent(event, mouseEvent);
        //     }}
        // }
      `}
      </style>
    </Box>
    </Paper>
    </>
  );
};

export default Events;